import React from 'react';
import { Users, Award, Sparkles, Heart, Gem, GraduationCap, CheckCircle2, Phone, MessageCircle } from 'lucide-react';
import { SALON, VALUES, FAMILY_TRUST, IMAGES, whatsappLink, telLink } from '../data/content';

const ICONS = { Users, Award, Sparkles, Heart, Gem, GraduationCap, CheckCircle2 };

const About = () => {
  return (
    <div>
      {/* Page Hero */}
      <section className="bg-gradient-to-b from-gold-soft/40 to-cream py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-5 md:px-8 text-center animate-fade-in-up">
          <span className="text-gold font-medium text-sm tracking-[0.25em] uppercase">About Us</span>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-bold mt-4 text-neutral-900">
            About {SALON.name}
          </h1>
          <div className="gold-divider mx-auto mt-6 mb-8" />
          <p className="text-lg md:text-xl text-neutral-600 leading-relaxed max-w-2xl mx-auto">
            Your trusted family salon in {SALON.city}, providing premium beauty and grooming
            services since our establishment.
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="relative order-2 lg:order-1">
              <div className="absolute -inset-4 bg-gold/10 rounded-3xl -rotate-2" />
              <img src={IMAGES.about} alt="Headtouch salon entrance" className="relative rounded-2xl shadow-xl w-full h-[500px] object-cover" />
            </div>
            <div className="order-1 lg:order-2">
              <span className="text-gold font-medium text-sm tracking-[0.25em] uppercase">Our Journey</span>
              <h2 className="font-serif text-4xl md:text-5xl font-bold mt-3 mb-5 text-neutral-900">Our Story</h2>
              <div className="gold-divider mb-6" />
              <div className="space-y-5 text-neutral-600 leading-relaxed">
                <p>
                  Headtouch was founded with a simple vision: to create a welcoming space where
                  families can enjoy professional salon services in a comfortable, relaxing
                  environment.
                </p>
                <p>
                  Located in the heart of {SALON.city}, we&apos;ve built our reputation on trust,
                  quality, and consistency. Our team of skilled professionals is dedicated to
                  helping you look and feel your best.
                </p>
                <p>
                  From precision haircuts to intricate nail art, from rejuvenating facials to
                  complete grooming services, we offer everything you need under one roof. Every
                  service is delivered with attention to detail and a commitment to excellence.
                </p>
                <p>
                  What sets us apart is our focus on building lasting relationships with our
                  clients. We take the time to understand your preferences, provide personalized
                  recommendations, and ensure you leave satisfied every time.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 md:py-24 bg-cream">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-gold font-medium text-sm tracking-[0.25em] uppercase">What We Stand For</span>
            <h2 className="font-serif text-4xl md:text-5xl font-bold mt-3 mb-5 text-neutral-900">Our Values</h2>
            <div className="gold-divider mx-auto mb-6" />
            <p className="text-neutral-600">The principles that guide everything we do</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {VALUES.map((v) => {
              const Icon = ICONS[v.icon];
              return (
                <div key={v.title} className="service-card bg-white rounded-2xl p-8 border border-gold/15 text-center">
                  <div className="w-16 h-16 mx-auto rounded-2xl bg-gold-soft flex items-center justify-center mb-5">
                    <Icon className="w-8 h-8 text-gold" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-serif text-xl font-semibold text-neutral-900 mb-3">{v.title}</h3>
                  <p className="text-sm text-neutral-600 leading-relaxed">{v.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Families Trust */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-gold font-medium text-sm tracking-[0.25em] uppercase">Our Promise</span>
            <h2 className="font-serif text-4xl md:text-5xl font-bold mt-3 mb-5 text-neutral-900">
              Why Families Trust Us
            </h2>
            <div className="gold-divider mx-auto" />
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {FAMILY_TRUST.map((item) => {
              const Icon = ICONS[item.icon];
              return (
                <div key={item.title} className="service-card bg-cream rounded-2xl p-8 border border-gold/15 text-center">
                  <div className="w-16 h-16 mx-auto rounded-2xl bg-white flex items-center justify-center mb-5 shadow-sm">
                    <Icon className="w-8 h-8 text-gold" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-serif text-2xl font-semibold text-neutral-900 mb-3">{item.title}</h3>
                  <p className="text-neutral-600 leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-gold to-gold-dark text-white">
        <div className="max-w-3xl mx-auto px-5 md:px-8 text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4">Experience Headtouch Yourself</h2>
          <p className="text-white/90 mb-8">Visit us and become part of our family</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={telLink} className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-white text-gold-dark rounded-full font-semibold shadow-lg btn-transition hover:shadow-xl">
              <Phone className="w-5 h-5" /> Call Now
            </a>
            <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-whatsapp text-white rounded-full font-semibold shadow-lg hover:bg-whatsapp-dark btn-transition">
              <MessageCircle className="w-5 h-5 fill-white" /> Message on WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
